import * as React from 'react';
import { FunctionComponent, useState, useEffect } from 'react';
import { AppUser } from 'Src/views/App/App';
import { HTMLEditorQuill, HTMLEditorQuillHandle } from '../HTMLEditorQuill/HTMLEditorQuill';
import {
  UserSignature,
  getUserSignatures,
  createUserSignature,
  updateUserSignature,
  deleteUserSignature
} from 'Src/helpers/service/admin/userSignatures';

interface Props {
  user: AppUser;
  isOpen: boolean;
  onClose: () => void;
}

export const UserSignatureManager: FunctionComponent<Props> = ({ user, isOpen, onClose }) => {
  const [signatures, setSignatures] = useState<UserSignature[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formName, setFormName] = useState('');
  const [formHtml, setFormHtml] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const editorRef = React.createRef<HTMLEditorQuillHandle>();

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen]);

  const load = () => {
    setIsLoading(true);
    getUserSignatures(user)
      .then(sigs => {
        setSignatures(sigs);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const startCreate = () => {
    setEditingId(null);
    setFormName('');
    setFormHtml('');
    setError('');
    setIsCreating(true);
  };

  const startEdit = (sig: UserSignature) => {
    setIsCreating(false);
    setEditingId(sig._id);
    setFormName(sig.name);
    setFormHtml(sig.htmlContent);
    setError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setIsCreating(false);
    setFormName('');
    setFormHtml('');
    setError('');
  };

  const save = () => {
    if (!formName.trim()) {
      setError('Name is required');
      return;
    }
    setIsSaving(true);
    const promise = isCreating
      ? createUserSignature(user, { name: formName, htmlContent: formHtml })
      : updateUserSignature(user, editingId!, { name: formName, htmlContent: formHtml });

    promise
      .then(() => {
        cancelEdit();
        load();
        setIsSaving(false);
      })
      .catch(() => {
        setError('Failed to save');
        setIsSaving(false);
      });
  };

  const remove = (id: string) => {
    if (!confirm('Delete this signature?')) return;
    deleteUserSignature(user, id)
      .then(() => load())
      .catch(() => {});
  };

  if (!isOpen) return null;

  const isEditing = isCreating || editingId !== null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1060,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)'
      }}
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '12px',
          width: '700px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <div style={{ fontWeight: 700, fontSize: '16px' }}>My signatures</div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
          {isLoading && <div style={{ color: '#64748b' }}>Loading...</div>}

          {/* Form */}
          {isEditing && (
            <div
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px',
                background: '#f8fafc'
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>
                {isCreating ? 'New signature' : 'Edit signature'}
              </div>
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="form-group">
                <label style={{ fontSize: '13px', fontWeight: 600 }}>Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  placeholder="e.g. Main signature, Formal, etc."
                  disabled={isSaving}
                />
              </div>
              <div className="form-group">
                <label style={{ fontSize: '13px', fontWeight: 600 }}>Content</label>
                <HTMLEditorQuill
                  user={user}
                  ref={editorRef}
                  value={formHtml}
                  onEmailBodyChange={setFormHtml}
                  isEnabled={!isSaving}
                  placeholder="Write your signature here..."
                />
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={cancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* List */}
          {!isLoading && signatures.length === 0 && !isEditing && (
            <div style={{ color: '#64748b', fontSize: '14px', marginBottom: '16px' }}>
              No signatures yet. Create your first one.
            </div>
          )}

          {signatures.map(sig => (
            <div
              key={sig._id}
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '10px',
                background: editingId === sig._id ? '#f0f9ff' : '#fff'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontWeight: 600, fontSize: '14px' }}>{sig.name}</div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => startEdit(sig)}>
                    Edit
                  </button>
                  <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => remove(sig._id)}>
                    Delete
                  </button>
                </div>
              </div>
              {sig.htmlContent && (
                <div
                  style={{
                    marginTop: '8px',
                    fontSize: '13px',
                    color: '#374151',
                    maxHeight: '80px',
                    overflow: 'hidden',
                    borderTop: '1px solid #f1f5f9',
                    paddingTop: '8px'
                  }}
                  dangerouslySetInnerHTML={{ __html: sig.htmlContent }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 24px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <button type="button" className="btn btn-primary btn-sm" onClick={startCreate} disabled={isEditing}>
            + New signature
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

UserSignatureManager.displayName = 'UserSignatureManager';
